/**
 * Telefoonnummers naar E.164 (ADR-0065, ADR-0099).
 *
 * Een telefoonveld is een landcode plus een nummer (`components/phone-field.vue`),
 * maar wat het veld aan zijn scherm doorgeeft is één internationale string:
 * `+32 0470 12 34 56`. Alles daarna -- de regel, het opslaan, de server -- leest
 * die ene string, zodat niemand het gekozen land hoeft te kennen. In
 * `users.phone_e164` staat er één vorm van: `+32470123456`.
 *
 * libphonenumber-js met de `max`-metadata: alleen die toetst de cijfers zelf en
 * niet alleen de lengte, zoals `giggsey/libphonenumber-for-php-lite` dat doet in
 * `App\Support\Contact\PhoneNumber`. Allebei zijn het ports van Googles
 * libphonenumber, en `scripts/test-phone-e164.mjs` en
 * `tests/Unit/Support/PhoneNumberTest.php` dragen dezelfde lijst gevallen: lopen
 * de twee uiteen, dan keurt het scherm iets goed dat de server weigert.
 */
import {
  getCountries,
  getCountryCallingCode,
  getExampleNumber,
  parsePhoneNumberFromString,
} from "libphonenumber-js/max";
import examples from "libphonenumber-js/mobile/examples";

/** Noemt de invoer geen land, dan is het nummer Nederlands (`06…`). */
export const DEFAULT_PHONE_COUNTRY = "NL";

/**
 * Wat er in een telefoonveld mag staan: cijfers, wat mensen ertussen zetten, en
 * vooraan een `+`. De bibliotheek vist zelf een nummer uit een zin ("bel
 * 0612345678"), en de PHP-port doet dat nét anders. Daarom staat deze regel aan
 * beide kanten, zodat ze allebei dezelfde invoer weigeren.
 */
const PHONE_CHARS = /^\+?[\d\s\-()./]+$/;

/** Nederland, België en Duitsland bovenaan: daar wonen vrijwel alle deelnemers. */
const PINNED_COUNTRIES = ["NL", "BE", "DE"];

function parse(value) {
  if (value === null || value === undefined) return null;

  const text = String(value).trim();
  if (!text || !PHONE_CHARS.test(text)) return null;

  const parsed = parsePhoneNumberFromString(text, DEFAULT_PHONE_COUNTRY);
  return parsed && parsed.isValid() ? parsed : null;
}

/**
 * Of deze bedrijfspagina om een telefoonnummer vraagt (ADR-0091). Afwezig of
 * null is aan: zo verandert er voor een bestaande pagina niets tot een admin de
 * schakelaar omzet. `ActivityController::asksPhone()` leest het net zo.
 */
export function asksPhone(page) {
  const flag = page?.settings?.ask_phone;
  return flag === undefined || flag === null ? true : !!flag;
}

/**
 * Het genormaliseerde nummer, of `null` als er niets bruikbaars in staat.
 * Een lege waarde is geen fout -- of het veld verplicht is, beslist het scherm.
 */
export function toE164(value) {
  return parse(value)?.number ?? null;
}

/** Of dit door de normalisatie komt. Een lege waarde telt als geldig. */
export function isNormalisablePhone(value) {
  if (value === null || value === undefined || String(value).trim() === "") return true;
  return toE164(value) !== null;
}

/**
 * Landcode en nummer als één internationale string. Noemt het nummer zelf al
 * een land (`+32…`, `0032…`), dan wint dat van de keuzelijst: zo plakt iemand
 * een nummer uit zijn contacten. De nationale nul mag blijven staan
 * (`+32 0470…`), die haalt de parser er zelf af waar het land dat doet.
 */
export function composePhone(country, national) {
  const text = String(national ?? "").trim();
  if (!text) return "";
  if (text.startsWith("+")) return text;
  if (text.startsWith("00")) return `+${text.slice(2)}`;
  return `+${getCountryCallingCode(country || DEFAULT_PHONE_COUNTRY)} ${text}`;
}

/**
 * Het omgekeerde, om een veld voor te vullen: `+32470123456` wordt België en
 * `0470 12 34 56`. Een nummer zonder land (`+800…`) houdt zijn internationale
 * vorm, en wat niet te lezen is blijft staan zoals het binnenkwam.
 */
export function splitPhone(value, fallbackCountry = DEFAULT_PHONE_COUNTRY) {
  const parsed = parse(value);
  if (!parsed) return { country: fallbackCountry, national: String(value ?? "").trim() };
  if (!parsed.country) return { country: fallbackCountry, national: parsed.formatInternational() };
  return { country: parsed.country, national: parsed.formatNational() };
}

/** Het land dat een nummer zelf noemt, of `null`. */
export function phoneCountryOf(value) {
  return parse(value)?.country ?? null;
}

/** Leesbaar: `+32 470 12 34 56`. Een `tel:`-link houdt E.164. */
export function formatPhone(value) {
  const parsed = parse(value);
  return parsed ? parsed.formatInternational() : value ?? "";
}

/** Een voorbeeldnummer in de schrijfwijze van dat land, voor de placeholder. */
export function examplePhone(country) {
  return getExampleNumber(country || DEFAULT_PHONE_COUNTRY, examples)?.formatNational() ?? "";
}

/** Alle landen voor de keuzelijst, met hun naam in de taal van het scherm. */
export function phoneCountries(locale) {
  let names = null;
  try {
    names = new Intl.DisplayNames([locale], { type: "region" });
  } catch {
    // Een browser zonder Intl.DisplayNames toont de landcode.
  }

  const all = getCountries().map((code) => ({
    code,
    dial: `+${getCountryCallingCode(code)}`,
    name: names?.of(code) || code,
  }));
  const pinned = PINNED_COUNTRIES.map((code) => all.find((country) => country.code === code));
  const rest = all
    .filter((country) => !PINNED_COUNTRIES.includes(country.code))
    .sort((a, b) => a.name.localeCompare(b.name, locale));

  return [...pinned, ...rest];
}

export default {
  asksPhone,
  toE164,
  isNormalisablePhone,
  composePhone,
  splitPhone,
  phoneCountryOf,
  formatPhone,
  examplePhone,
  phoneCountries,
};
